import React, { ReactElement, useState } from 'react';
import { TeamMembership } from '@enview/interface/types/Team';
import MagnifyingGlass from '../../../components/svg/MagnifyingGlass';
import { getDisplayName } from '../../../helpers/AvatarHelper';
import './TeamMemberSelectorContent.scss';

const matchesSearch = (member: TeamMembership, search: string): boolean => {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return (
    getDisplayName(member).toLowerCase().includes(query) ||
    (member.email ?? '').toLowerCase().includes(query)
  );
};

interface TeamMemberSelectorContentProps {
  /** Members of the workspace the assignment is being made in */
  members: TeamMembership[];
  /** Currently assigned organization-user ids */
  assigneeIds: number[];
  /** Called with the full set of selected organization-user ids when Save is clicked */
  onSave: (assigneeIds: number[]) => void;
  /** Heading shown above the member list, e.g. "ASSIGN TO..." */
  sectionLabel?: string;
  /** Disables Save while a previous save from this same picker is still in flight */
  saving?: boolean;
}

// The shared search + multi-select member list used by the Assignee column and
// bulk assign action — mirrors WorkspaceSelectorContent's search/list/select-all/Save
// shape. Callers own the popover shell
// (trigger + open/close state) and should only mount this while it's open, so search
// and selection state start fresh each time.
const TeamMemberSelectorContent = (
  props: TeamMemberSelectorContentProps,
): ReactElement => {
  const {
    members,
    assigneeIds,
    onSave,
    sectionLabel = 'ASSIGN TO...',
    saving = false,
  } = props;

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>(assigneeIds);

  const filteredMembers = members.filter((member) => matchesSearch(member, search));

  const toggleMember = (organizationUserId: number): void => {
    setSelectedIds((prevSelectedIds) =>
      prevSelectedIds.includes(organizationUserId)
        ? prevSelectedIds.filter((id) => id !== organizationUserId)
        : [...prevSelectedIds, organizationUserId],
    );
  };

  const handleSelectAll = (): void => {
    const filteredIds = filteredMembers.map((member) => member.organizationUserId);
    setSelectedIds((prevSelectedIds) =>
      Array.from(new Set([...prevSelectedIds, ...filteredIds])),
    );
  };

  const handleDeselectAll = (): void => {
    const filteredIds = filteredMembers.map((member) => member.organizationUserId);
    setSelectedIds((prevSelectedIds) =>
      prevSelectedIds.filter((id) => !filteredIds.includes(id)),
    );
  };

  const handleSave = (): void => {
    onSave(selectedIds);
  };

  return (
    <div className="team-member-selector">
      <div className="tms-search">
        <MagnifyingGlass height="14" width="14" />
        <input
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search team members..."
          type="text"
          value={search}
        />
      </div>

      <div className="tms-section-label">{sectionLabel}</div>

      <div className="tms-list">
        {filteredMembers.map((member) => {
          const selected = selectedIds.includes(member.organizationUserId);
          return (
            <label
              className={`tms-list-item ${selected ? 'selected' : ''}`}
              key={member.organizationUserId}
            >
              <input
                checked={selected}
                disabled={saving}
                onChange={() => toggleMember(member.organizationUserId)}
                type="checkbox"
              />
              <span className="tms-name">{getDisplayName(member)}</span>
            </label>
          );
        })}

        {filteredMembers.length === 0 && (
          <div className="tms-empty">No team members found</div>
        )}
      </div>

      <div className="tms-select-actions">
        <button disabled={saving} onClick={handleSelectAll} type="button">
          Select all
        </button>
        <span className="tms-select-actions-divider">·</span>
        <button disabled={saving} onClick={handleDeselectAll} type="button">
          Deselect all
        </button>
      </div>

      <button className="tms-save" disabled={saving} onClick={handleSave} type="button">
        Save
      </button>
    </div>
  );
};

export default TeamMemberSelectorContent;
